import { ComponentTemplates } from '../util/component-templates.js';
import { ownsRecordClick } from '../util/record-click.js';
/**
 * <g-table> - Data Table with Sort + Pagination
 *
 * Renders a sortable, paginated table from a JS data array. Light DOM —
 * the host can style cells freely with Tailwind/DaisyUI. Filtering and
 * data fetching stay in the host (Alpine component); this element owns
 * rendering + sort + paginate + row-click.
 *
 * @example
 *   <g-table id="t" page-size="20" empty-text="No accounts found"></g-table>
 *   <script>
 *     const t = document.getElementById('t');
 *     t.columns = [
 *       { field: 'Customer Name', label: 'Customer', sortable: true, classes: 'font-medium' },
 *       { field: 'Original Balance', label: 'Original',
 *         render: 'currency', currency: 'USD', sortable: true },
 *       { field: 'Status', label: 'Status',
 *         render: 'badge', colorMap: { Active: 'success', Pending: 'warning', Closed: 'neutral' } },
 *       { field: 'Last Payment Date', label: 'Last Payment', render: 'date', sortable: true }
 *     ];
 *     t.data = accounts;
 *     t.addEventListener('row-click', (e) => openDetail(e.detail.row));
 *   </script>
 *
 * Properties (set via JS, not attributes):
 *   .columns  Array<ColumnDef> — see field shape below
 *   .data     Array<object>    — row records (keys match column.field exactly);
 *                                ignored while `source` is set
 *   .where    (record) => boolean | record predicate — which records show
 *   .mapRow   (record) => row — display transform, memoised per record; keep `_id`
 *   .collection  the shared collection behind `source` (read-only), or null
 *
 * Attributes:
 *   page-size    integer (default 20)
 *   empty-text   string  (default "No records found")
 *   loading      boolean (presence = skeleton rows under the real headers)
 *   readonly     boolean (presence = no cell is editable, whatever the columns say)
 *   source       same-origin endpoint ("/api/deals"): load rows from the shared
 *                collection (src/data/collection.js) instead of `.data`. An
 *                unhandled `cell-edit` then saves through it. See docs/data-source.md.
 *   live         socket entity ("deal"): follow deal:created|updated|deleted
 *   server-total integer (OPT-IN server pagination: `.data` is ONE host-
 *                fetched page, pre-filtered/pre-sorted; the component skips
 *                its internal slice + sort, shows "x–y of <server-total>",
 *                and page/sort clicks only emit page-change / sort-change
 *                for the host to refetch. Absent → classic client mode.)
 *
 * ColumnDef:
 *   { field: string,                     // exact key into row
 *     label?: string,                    // header text (defaults to field)
 *     sortable?: boolean,                // default false
 *     classes?: string,                  // extra <td> classes
 *     render?: 'text'|'currency'|'number'|'percent'|'date'|'datetime'|'boolean'|'badge'|'ref-link'|'actions',
 *     currency?: string,                 // for render:'currency' (default 'USD')
 *                                        // render:'percent' takes a 0–1 ratio: 0.93 → "93.0%"
 *     colorMap?: Record<string,string>,  // for render:'badge' — value → daisy color
 *     displayField?: string,             // for render:'ref-link' — key on populated ref
 *     actions?: Array<string | {         // for render:'actions' — per-row buttons
 *       id: string,                      //   dispatched as detail.action
 *       label?: string,                  //   tooltip + aria-label (default: capitalised id)
 *       icon?: 'edit'|'delete'|'view'|'duplicate'|'archive'|'more',
 *       color?: Status                   //   semantic token only
 *     }>
 *   }
 *   An actions column needs no `field` — row identity comes from the row itself.
 *   Right-aligned and non-sortable by default; its header is blank unless you
 *   set `label`.
 *
 * Search (driven by <g-panel>, or by any host that calls it):
 *   .setSearch(q)     filters in client mode; in server mode emits search-change
 *                     instead, because `.data` is one page and filtering it
 *                     would search a window while looking like it searched all
 *   .searchFields     which fields to match (default: every visible column)
 *   .searchRelevant   whether a box is worth showing — more rows than fit a
 *                     page, or a query already live
 *
 * Events:
 *   row-click    detail: { row, index }   — NOT fired when the click landed on a
 *                button/link/input, which is what lets a row be a selection
 *                target while still carrying per-row action buttons
 *   row-action   detail: { action, row, index }
 *   cell-edit    detail: { field, value, previousValue, row, index, column, respondWith }
 *                cancelable; only from `editable` columns — see Inline editing
 *   sort-change  detail: { field, direction }
 *   page-change  detail: { page, totalPages, pageSize }
 *   search-change detail: { query }        — server mode only; refetch
 *   content-change detail: { total, rows, searchRelevant } — after every render,
 *                 for the chrome around the table
 *
 * Inline editing (opt-in per column — a table with no `editable` column is
 * read-only exactly as before):
 *
 *   t.columns = [
 *     { field: 'name', label: 'Item' },
 *     { field: 'spent', label: 'Spent', render: 'currency', editable: true, min: 0 },
 *     { field: 'status', label: 'Status', render: 'badge', editable: (row) => !row.locked,
 *       colorMap: { Open: 'info', Closed: 'neutral' } },
 *   ];
 *   t.addEventListener('cell-edit', (e) => {
 *     const { row, field, value } = e.detail;
 *     e.detail.respondWith(api.patch(row._id, { [field]: value }));  // optional
 *   });
 *
 *   ColumnDef keys:
 *     editable?: boolean | (row) => boolean
 *     editor?:   'text'|'textarea'|'number'|'currency'|'percent'|'date'|'datetime'|'select'|'boolean'
 *                (default: inferred from `render` — currency edits as a number,
 *                badge as a select over its colorMap, boolean toggles on click)
 *     options?:  Array<string | { value, label }>   // select; default colorMap keys
 *     required?: boolean            min?/max?: number (percent: in shown units, 0–100)
 *     validate?: (value, row) => string | false | null   // a string is the error shown
 *   `ref-link` and `actions` columns are never editable.
 *
 *   Click (or Enter/F2 on a focused cell) opens the editor; Enter or clicking
 *   away saves, Escape cancels, Tab saves and moves to the next editable cell.
 *   An editable cell does not fire row-click. The `readonly` attribute turns
 *   every editable column off at once.
 *
 *   On save the table fires `cell-edit` (cancelable — preventDefault() vetoes
 *   the edit), then writes `value` into the row object and re-renders, so
 *   totals and sort follow. `respondWith(promise)` — called synchronously in
 *   the listener — marks the cell as saving; if the promise rejects, the old
 *   value is put back and the cell is outlined with the error as its tooltip.
 *
 * Row status (row background):
 *   .rowStatus      (row, index) => Status | null
 *   .rowStatusFrom  { field, colorMap?, colorRule? }
 *   Status = 'primary'|'secondary'|'accent'|'info'|'success'|'warning'|'error'|'neutral'
 *   The component maps the status to a tint itself, so a caller cannot pass an
 *   absolute colour that stops reading after a theme switch. See util/status.js.
 */
import { resolveStatus, statusAttr, ensureStatusStyles } from '../util/status.js';
import { aggregate } from '../util/aggregate.js';
import { escapeHtml, renderActionButtons } from '../util/actions.js';
import { editorFor, isEditable, draftFor, parseDraft, sameValue, toggled, renderEditor, reconcileOpenEdit } from './cell-edit.js';
import { FLASH_MS, signature, snapshotRows, changedCells, flashKey } from './cell-flash.js';
import { getField, refLabel } from '../util/fields.js';
import { evalRecord } from '../util/predicate.js';
import { calendarDate } from '../util/dates.js';
import { compareText } from '../util/collate.js';
import { SourceBinding } from '../data/collection.js';

// Boolean cell: green check for truthy, red x for falsy. Null / undefined /
// empty string → em-dash (no answer yet, distinct from "answered no").
//
// Font Awesome, like every icon in this stack. These were hand-drawn SVG for a
// long time, justified by a claim that FA's MutationObserver misses icons
// inserted during an innerHTML replace — the render path this table uses on
// every data refresh. The claim was wrong: g-autopilot renders `fa-regular`
// icons throughout its own innerHTML and has shipped that way in production.
//
// The cost was not the pixels. The rationale got cited and copied into
// g-card-grid, and a second, hand-drawn icon vocabulary started growing inside
// components whose whole purpose is to stop exactly that.

// Row hover. DaisyUI renamed this class in v5: the shipped stylesheet defines
// `.table tr.row-hover` (and a `.table-zebra` variant) and contains no bare
// `.hover` rule at all, so the `hover` class this element emitted before was
// inert — rows had silently had no hover highlight since the v4 → v5 upgrade.
// Both are emitted so the highlight works on either major version; the v4 name
// is undefined under v5 and simply does nothing.
const ROW_HOVER_CLASS = 'row-hover hover';

const TOTALS = new Set(['sum', 'avg', 'count', 'min', 'max']);

// Date instance, or a string that starts ISO-shaped (YYYY-MM-DD).
const isDateish = (v) => v instanceof Date || (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v));

// A count is a cardinality, not a measure: routed through a currency renderer it
// would print "$3.00". Everything else formats like its own column.
function formatTotal(value, col) {
  if (!Number.isFinite(value)) return '—';
  if (col.total === 'count') return RENDERERS.number(value, col);
  const fn = RENDERERS[col.render] || RENDERERS.number;
  return fn(value, col);
}

const SVG_CHECK = `<i class="fa-regular fa-circle-check" aria-hidden="true"></i>`;
const SVG_XMARK = `<i class="fa-regular fa-circle-xmark" aria-hidden="true"></i>`;

// Row-action glyphs, tones and button markup live in util/actions.js.
//
// The first version hand-drew these as inline SVG, on the theory that the
// bundle could not assume the host had loaded an icon font. Wrong on the fact
// (the app shell loads the Font Awesome kit) and wrong on the principle: a
// second, hand-drawn icon vocabulary living inside one component is exactly the
// drift the shared catalog exists to prevent. Every icon in a generated app is
// `fa-regular`; these are too.
//
// They moved out of this file when g-panel needed the same vocabulary for its
// header buttons — the exact moment at which copying them would have started
// that second vocabulary for real.

const renderBoolean = (v) => {
  if (v == null || v === '') return '—';
  // Treat string forms ("true" / "false") the same as real booleans —
  // upstream sometimes serializes via JSON.stringify before reaching the
  // table (e.g., when piped through a generic text renderer fallback).
  const truthy = v === true || v === 1 || v === '1' || v === 'true' || v === 'yes';
  const falsy = v === false || v === 0 || v === '0' || v === 'false' || v === 'no';
  if (!truthy && !falsy) return escapeHtml(String(v));
  return truthy
    ? `<span class="text-success inline-flex" title="Yes" aria-label="Yes">${SVG_CHECK}</span>`
    : `<span class="text-error inline-flex" title="No" aria-label="No">${SVG_XMARK}</span>`;
};

// A float that arithmetic left ragged — a derived total of 13216.000000000002 —
// shows at most two decimals. Integers and text pass through untouched.
const roundFloat = (v) => (typeof v === 'number' && Number.isFinite(v) && !Number.isInteger(v)
  ? Math.round(v * 100) / 100
  : v);

const RENDERERS = {
  text: (v) => {
    // Auto-promote raw booleans to the icon renderer so columns that didn't
    // get tagged with render:'boolean' still display correctly. Strings
    // "true" / "false" are NOT promoted here — they may legitimately be
    // labels for something else; opt-in via render:'boolean'.
    if (v === true || v === false) return renderBoolean(v);
    // A populated ref read straight off an API record (`source` mode, where no
    // host flattened it first) shows its name, never "[object Object]".
    if (Array.isArray(v)) {
      const parts = v.map((x) => refLabel(x)).filter((x) => x != null && x !== '');
      return parts.length ? escapeHtml(parts.join(', ')) : '—';
    }
    if (v && typeof v === 'object') v = refLabel(v);
    return v == null || v === '' ? '—' : escapeHtml(String(roundFloat(v)));
  },
  number: (v) => v == null || v === '' ? '—' : escapeHtml(String(roundFloat(v))),
  // A 0–1 ratio, scaled once here — the same convention as g-kpi and
  // g-card-grid, so one contract value means one thing on every component.
  percent: (v) => {
    const n = typeof v === 'number' ? v : parseFloat(v);
    if (v == null || v === '' || isNaN(n)) return '—';
    return escapeHtml((n * 100).toFixed(1) + '%');
  },
  boolean: renderBoolean,
  currency: (v, col) => {
    const n = parseFloat(v);
    if (isNaN(n)) return '—';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: col.currency || 'USD' }).format(n);
    } catch { return String(v); }
  },
  date: (v) => {
    if (!v) return '—';
    const d = calendarDate(v);
    if (!d) return escapeHtml(String(v));
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
  },
  datetime: (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d.getTime())) return escapeHtml(String(v));
    return d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  },
  badge: (v, col) => {
    if (v == null || v === '') return '—';
    const map = col.colorMap || {};
    const colorKey = map[v] || 'neutral';
    const cls = `badge badge-soft badge-sm badge-${colorKey}`;
    return `<span class="${cls}">${escapeHtml(String(v))}</span>`;
  },
  // A column of per-row buttons. Value-independent: row identity comes from the
  // enclosing `<tr data-row-index>`, which the click handler already reads, so
  // an actions column needs no `field`.
  //
  // This pairs with the guard in _handleClick that refuses to fire `row-click`
  // when the click landed on a button. That guard is what lets a table use the
  // row itself as a selection target — driving a second panel, say — while
  // still offering edit and delete per row. Until this renderer existed there
  // was no way to put a control in a row at all, so a host had to choose
  // between the two behaviours.
  actions: (v, col) => {
    const acts = Array.isArray(col.actions) ? col.actions : [];
    if (acts.length === 0) return '';
    // Ghost + square + tinted glyph: a row button is secondary to reading the
    // row. `tinted` is what keeps that true — a solid `btn-error` on every row
    // would shout louder than the data.
    const buttons = renderActionButtons(acts, {
      attr: 'data-row-action',
      className: 'btn btn-ghost btn-xs btn-square',
      tinted: true,
    });
    // `data-g-actions` is the hook the stylesheet uses to keep these hidden
    // until the row is hovered or focused — a button on every row of a long
    // table is a wall of icons, and the actions are secondary to reading it.
    return buttons ? `<span class="inline-flex items-center gap-1" data-g-actions>${buttons}</span>` : '';
  },
  'ref-link': (v, col) => {
    if (v == null || v === '') return '—';
    const key = col.displayField || 'name';
    const label = (x) => (x && typeof x === 'object') ? x[key] : x;
    // Multi-ref (objectIdArray relation, e.g. an Owner column populated as
    // [{_id, name}, …]) renders each populated ref as a soft chip, matching
    // the detail drawer's chip picker. Single ref keeps the plain-text form.
    if (Array.isArray(v)) {
      const chips = v
        .map(label)
        .filter((x) => x != null && x !== '')
        .map((n) => `<span class="badge badge-soft badge-sm">${escapeHtml(String(n))}</span>`);
      return chips.length ? chips.join(' ') : '—';
    }
    const display = (typeof v === 'object') ? v[key] : v;
    return escapeHtml(String(display ?? '—'));
  },
};

// escapeHtml is imported from util/actions.js — one definition for the whole
// bundle rather than one per component.

// Column presentation. These are emitted as data-* attributes backed by the
// stylesheet in util/status.js rather than as Tailwind utilities: `text-right`,
// `md:table-cell` and arbitrary values like `max-w-[40ch]` are core Tailwind,
// and nothing in a host app scans this bundle to generate them.
const ALIGNS = new Set(['left', 'right', 'center']);
const BREAKPOINTS = new Set(['sm', 'md', 'lg']);
// Right-align numeric columns by default — today every caller hand-writes
// `classes: 'text-right'`. An explicit `align` still wins.
const NUMERIC_RENDERS = new Set(['currency', 'number', 'percent']);
// Actions sit at the end of a row and read best hard against the edge.
const RIGHT_ALIGNED_RENDERS = new Set([...NUMERIC_RENDERS, 'actions']);

function cellAttrs(col, { header = false, extraStyle = null } = {}) {
  const out = [];
  const style = [];
  if (extraStyle) style.push(extraStyle);
  if (col.classes && !header) out.push(` class="${escapeHtml(col.classes)}"`);
  const align = ALIGNS.has(col.align)
    ? col.align
    : (RIGHT_ALIGNED_RENDERS.has(col.render) ? 'right' : null);
  if (align && align !== 'left') out.push(` data-align="${align}"`);
  // Must be applied to th, td AND the totals cell, or the column misaligns.
  if (BREAKPOINTS.has(col.hideBelow)) out.push(` data-hide-below="${col.hideBelow}"`);
  if (col.truncate && !header) {
    const ch = Number.isFinite(+col.truncate) && +col.truncate > 0 ? +col.truncate : 40;
    out.push(' data-truncate');
    style.push(`max-width:${ch}ch`);
  }
  // Width belongs on the header cell; the column inherits it.
  if (header && typeof col.width === 'string' && /^\d+(\.\d+)?(px|rem|em|%)$/.test(col.width)) {
    style.push(`width:${col.width}`);
  }
  if (style.length) out.push(` style="${style.join(';')}"`);
  return out.join('');
}

export function renderCell(row, col) {
  const raw = getField(row, col.field);
  const fn = RENDERERS[col.render] || RENDERERS.text;
  return fn(raw, col);
}

/**
 * A cell value as lowercase text a query can be matched against.
 *
 * Populated refs are unwrapped to the same display value the cell shows —
 * matching against `[object Object]` would make a ref column silently
 * unsearchable, which reads as the search being broken rather than the column
 * being excluded. Dates are matched on their rendered form as well as the raw
 * ISO string, so both "2026-09" and "Sep" find the same row.
 */
function searchText(v, col) {
  if (v == null || v === '') return '';
  if (Array.isArray(v)) return v.map((x) => searchText(x, col)).join(' ');
  if (typeof v === 'object') {
    const key = (col && col.displayField) || 'name';
    return String(v[key] ?? v.title ?? v.label ?? '').toLowerCase();
  }
  const s = String(v).toLowerCase();
  if (col && (col.render === 'date' || col.render === 'datetime') && isDateish(v)) {
    const fn = RENDERERS[col.render];
    return `${s} ${String(fn(v, col)).toLowerCase()}`;
  }
  return s;
}

export class GTable extends HTMLElement {
  constructor() {
    super();
    this._columns = [];
    this._data = [];
    this._where = null;          // (row) => boolean, or a record predicate
    this._mapRow = null;         // (record) => display row
    this._mapped = new WeakMap(); // record -> its mapped row (records are immutable under `source`)
    this._rowsCache = null;
    this._rowStatus = null;
    this._selectedId = null;
    this._totals = null;
    this._sortTouched = false;
    this._rowStatusFrom = null;
    this._sort = { field: null, direction: 'asc' }; // persists across .data refresh
    this._page = 1;
    this._search = '';
    this._searchFields = null;
    this._renderScheduled = false;
    this._templates = new ComponentTemplates(this);
    // Inline editing. One open cell at a time; per-cell save state is keyed on
    // the row object so it follows the row through sort, search and paging.
    this._editing = null;        // { row, id, field, draft, initial, error }
    this._cellState = new WeakMap(); // row -> Map(field -> { saving, token } | { error })
    this._afterRender = null;    // what to focus once the next render lands
    this._cellStateById = new Map(); // same, for rows with an _id — survives the
                                     // record being replaced by a live update
    // Highlight on cells a data update changed (cell-flash.js).
    this._dataVersion = 0;
    this._diffedVersion = 0;
    this._valueSnap = null;      // id -> Map(field -> signature) as of the last update
    this._flash = new Map();     // flashKey -> start time
    // `source` / `live`: the table loads and follows its own rows.
    this._sourceLoading = false;
    this._sourceError = null;
    this._source = new SourceBinding(this, (c) => {
      this._sourceLoading = c.status === 'loading' && c.records.length === 0;
      this._sourceError = c.status === 'error' && c.records.length === 0 ? c.error : null;
      this._setData(c.records);
    });
  }

  static get observedAttributes() {
    return [
      'page-size', 'empty-text', 'loading', 'server-total',
      'size', 'zebra', 'pin-rows', 'max-height',
      'no-row-click', 'no-hover', 'sort-field', 'sort-dir', 'empty-icon',
      'readonly', 'source', 'live',
    ];
  }

  get size() {
    const s = this.getAttribute('size');
    return ['xs', 'sm', 'md', 'lg'].includes(s) ? s : 'sm';
  }

  get noRowClick() { return this.hasAttribute('no-row-click'); }
  get noHover() { return this.hasAttribute('no-hover'); }
  // Turns off every `editable` column at once — for a viewer role, say —
  // without the host rebuilding its column definitions.
  get readonly() { return this.hasAttribute('readonly'); }
  get emptyIcon() {
    const v = this.getAttribute('empty-icon');
    // Lands in a class attribute, so keep it to icon-shaped tokens.
    return v && /^[\w\s-]+$/.test(v) ? v : 'fa-regular fa-inbox';
  }

  // Host-supplied value reaching a `style` attribute — the only one in this
  // component — so it is validated rather than escaped. Anything else is dropped.
  get maxHeight() {
    const v = this.getAttribute('max-height');
    return v && /^\d+(\.\d+)?(px|rem|em|vh|%)$/.test(v) ? v : null;
  }

  attributeChangedCallback(name) {
    if ((name === 'source' || name === 'live') && this.isConnected) this._source.sync();
    this.scheduleRender();
  }

  connectedCallback() {
    this.classList.add('block');
    this._templates.connect();
    ensureStatusStyles();

    // Guard against double-attach. <g-tabs> reparents its children into a
    // panel-host wrapper, which triggers disconnect → connect on every
    // nested custom element. Without this guard, every reattach left a
    // stale `_root` div in the DOM showing whatever empty/loading state
    // was current at first attach (e.g. "No X found" + "No results"
    // pagination) while a fresh _root rendered the real data — both visible.
    if (!this._root || this._root.parentNode !== this) {
      // Drop any stale root divs from a prior attach cycle.
      [...this.children].forEach(c => {
        if (c.dataset && c.dataset.gtableRoot === '') c.remove();
      });
      this._root = document.createElement('div');
      this._root.dataset.gtableRoot = '';
      this.appendChild(this._root);
    }

    if (this.hasAttribute('source') && this.hasAttribute('server-total')) {
      console.warn('[g-table] `source` loads the whole collection; `server-total` is ignored with it.');
    }
    this._source.sync();
    this.scheduleRender();
    this.addEventListener('click', this._handleClick);
    this.addEventListener('keydown', this._onKeydown);
    this.addEventListener('input', this._onInput);
    this.addEventListener('change', this._onChange);
    this.addEventListener('focusout', this._onFocusOut);
  }

  disconnectedCallback() {
    this._source.stop();
    this._templates.disconnect();
    this.removeEventListener('click', this._handleClick);
    this.removeEventListener('keydown', this._onKeydown);
    this.removeEventListener('input', this._onInput);
    this.removeEventListener('change', this._onChange);
    this.removeEventListener('focusout', this._onFocusOut);
  }

  // ── Public API ──

  get columns() { return this._columns; }
  set columns(cols) {
    this._columns = Array.isArray(cols) ? cols : [];
    this.scheduleRender();
  }

  // Row tint. Either a callback — .rowStatus = (row, i) => 'error' | null — or
  // the declarative twin .rowStatusFrom = { field, colorMap, colorRule },
  // mirroring ColumnDef's badge colouring one level up at the row. The callback
  // wins when both are set. Values are constrained to semantic tokens, so a
  // caller cannot express an absolute colour that dies on a theme switch.
  // Host-supplied totals, the only correct answer in server-pagination mode:
  // { fieldName: number }. Wins over anything computed locally.
  get totals() { return this._totals; }
  set totals(v) { this._totals = v && typeof v === 'object' ? v : null; this.scheduleRender(); }

  // Which row is the current selection. A separate channel from rowStatus:
  // "the row you picked" and "the row that is over budget" are different
  // facts and a row can be both, so selection must not consume the status
  // token. Compared as a string, so an ObjectId and its string form match.
  get selectedId() { return this._selectedId; }
  set selectedId(v) { this._selectedId = (v === null || v === undefined || v === '') ? null : String(v); this.scheduleRender(); }

  get rowStatus() { return this._rowStatus; }
  set rowStatus(fn) { this._rowStatus = typeof fn === 'function' ? fn : null; this.scheduleRender(); }

  get rowStatusFrom() { return this._rowStatusFrom; }
  set rowStatusFrom(spec) { this._rowStatusFrom = spec && typeof spec === 'object' ? spec : null; this.scheduleRender(); }

  _statusSpec() { return this._rowStatus || this._rowStatusFrom || null; }

  get data() { return this._data; }
  set data(rows) {
    // With `source`, the collection owns the rows; a host assignment would be
    // overwritten by the next live update anyway, so it is refused up front.
    if (this._source && this._source.active) return;
    this._setData(rows);
  }

  /** The shared collection behind `source`, or null — for a host that wants the records too. */
  get collection() { return this._source.collection; }

  /**
   * Which records the table shows: a function `(record) => boolean`, or a
   * record predicate in the same shape g-kpi takes
   * (`{ field: 'status.name', op: { nin: ['Closed'] } }`). It sees the record
   * as loaded, before `mapRow`. This is how a host keeps its own filters —
   * dropdowns, pills, a selected parent — over a `source` table it no longer
   * feeds: the rows stay live, the host only says which of them count.
   */
  get where() { return this._where; }
  set where(w) {
    this._where = typeof w === 'function' || (w && typeof w === 'object') ? w : null;
    this._invalidateRows();
  }

  /**
   * `(record) => row` applied before display, sort and search — for a host
   * that already has its own display rules (a generated app's ref flattening)
   * and wants them kept exactly. Memoised per record object, so a live update
   * re-maps only the record that changed. The mapped row must keep `_id`:
   * edits, selection and live updates follow rows by it.
   */
  get mapRow() { return this._mapRow; }
  set mapRow(fn) {
    this._mapRow = typeof fn === 'function' ? fn : null;
    this._mapped = new WeakMap();
    // Mapped values change shape with the mapper; that is not a data update.
    this._valueSnap = null;
    this._invalidateRows();
  }

  _invalidateRows() {
    this._rowsCache = null;
    const total = this._totalPages();
    if (this._page > total) this._page = total;
    this.scheduleRender();
  }

  /** `.data` after `where` and `mapRow` — what every other step works on. */
  _rows() {
    if (this._rowsCache) return this._rowsCache;
    let rows = this._data;
    const w = this._where;
    if (w) {
      const test = typeof w === 'function'
        ? (r) => { try { return !!w(r); } catch (err) { console.error('[g-table] where threw:', err); return false; } }
        : (r) => evalRecord(r, w);
      rows = rows.filter(test);
    }
    const map = this._mapRow;
    if (map) {
      rows = rows.map((r) => {
        if (!r || typeof r !== 'object') return r;
        let m = this._mapped.get(r);
        if (!m) {
          try { m = map(r) || r; } catch (err) { console.error('[g-table] mapRow threw:', err); m = r; }
          this._mapped.set(r, m);
        }
        return m;
      });
    }
    this._rowsCache = rows;
    return rows;
  }

  _setData(rows) {
    this._rowsCache = null;
    this._data = Array.isArray(rows) ? rows : [];
    this._dataVersion++;
    // Clamp page if data shrank. _totalPages() respects server-total, so a
    // server-mode page assignment (always ≤ page-size rows) never snaps the
    // current page back to 1.
    const total = this._totalPages();
    if (this._page > total) this._page = total;
    this.scheduleRender();
  }

  get pageSize() {
    const n = parseInt(this.getAttribute('page-size'), 10);
    return Number.isFinite(n) && n > 0 ? n : 20;
  }

  // Opt-in server pagination: when `server-total` is set, `.data` is ONE
  // page (already filtered/sorted by the host) — the component renders it
  // without slicing or client-sorting, shows "x–y of <server-total>", and
  // page/sort interactions only emit `page-change`/`sort-change` for the
  // host to refetch. Absent attribute (every existing app) → behavior is
  // unchanged. null = not in server mode.
  get serverTotal() {
    if (!this.hasAttribute('server-total') || this.hasAttribute('source')) return null;
    const n = parseInt(this.getAttribute('server-total'), 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }

  get emptyText() {
    return this.getAttribute('empty-text') || 'No records found';
  }

  get loading() {
    return this.hasAttribute('loading') || this._sourceLoading;
  }

  setSort(field, direction) {
    this._sortTouched = true;
    this._sort = { field, direction: direction === 'desc' ? 'desc' : 'asc' };
    this.scheduleRender();
    this.dispatchEvent(new CustomEvent('sort-change', {
      detail: { ...this._sort }, bubbles: true, composed: true,
    }));
  }

  setPage(page) {
    const totalPages = this._totalPages();
    const next = Math.max(1, Math.min(page, totalPages));
    if (next === this._page) return;
    this._page = next;
    this.scheduleRender();
    this.dispatchEvent(new CustomEvent('page-change', {
      detail: { page: this._page, totalPages, pageSize: this.pageSize }, bubbles: true, composed: true,
    }));
  }

  // ── Search ──
  //
  // The table filters its own rows in client mode and only reports the query in
  // server mode — the same split it already makes for sort and pagination, and
  // for the same reason: with `server-total` set, `.data` is one fetched page,
  // so filtering it here would search a window and present the result as if it
  // had searched the dataset.
  //
  // The input itself is not here. <g-panel> renders it in the panel header and
  // calls setSearch(), which is what lets one chrome drive a table, a card grid
  // or anything else that grows this method. Hosts that want their own input
  // can call it directly; nothing about the table assumes a panel.

  /** Which fields a query is matched against. Defaults to every visible column. */
  get searchFields() {
    if (Array.isArray(this._searchFields) && this._searchFields.length) return this._searchFields;
    return this._columns.filter((c) => c && c.field && c.render !== 'actions').map((c) => c.field);
  }
  set searchFields(v) { this._searchFields = Array.isArray(v) ? v : null; this.scheduleRender(); }

  get search() { return this._search || ''; }

  setSearch(q) {
    const next = typeof q === 'string' ? q.trim() : '';
    if (next === this.search) return;
    this._search = next;
    // A query is a new result set, so the page the user was on no longer means
    // anything. Not resetting it is how a search lands on an empty page 4.
    this._page = 1;
    this.scheduleRender();
    if (this.serverTotal != null) {
      this.dispatchEvent(new CustomEvent('search-change', {
        detail: { query: next }, bubbles: true, composed: true,
      }));
    }
  }

  /**
   * Whether a search box earns its place on this table.
   *
   * Measured against the UNFILTERED row count, and sticky once a query is
   * live: deciding from the filtered count would make the box disappear the
   * moment it narrowed the list past a page, taking the user's own query with
   * it and leaving no way to clear it.
   */
  get searchRelevant() {
    if (this.search) return true;
    const total = this.serverTotal != null ? this.serverTotal : this._rows().length;
    return total > this.pageSize;
  }

  // ── Internals ──

  /** The rows a query leaves standing. Server mode: the host already filtered. */
  _filteredData() {
    const q = this.search.toLowerCase();
    if (!q || this.serverTotal != null) return this._rows();
    const fields = this.searchFields;
    if (!fields.length) return this._rows();
    const byField = new Map(this._columns.filter((c) => c && c.field).map((c) => [c.field, c]));
    return this._rows().filter((row) => fields.some((f) => searchText(getField(row, f), byField.get(f)).includes(q)));
  }

  _totalPages() {
    const total = this.serverTotal != null ? this.serverTotal : this._filteredData().length;
    return Math.max(1, Math.ceil(total / this.pageSize));
  }

  // Initial sort is an EFFECTIVE value rather than a mutation on mount: it
  // sidesteps every connect/attribute ordering hazard, guarantees no
  // sort-change fires on init (the host has not asked for a refetch), and
  // survives the <g-tabs> reparent cycle. Public so a server-mode host can read
  // what to honour in its first fetch.
  get sort() {
    if (this._sortTouched || this._sort.field) return { ...this._sort };
    const field = this.getAttribute('sort-field');
    if (!field) return { ...this._sort };
    return { field, direction: this.getAttribute('sort-dir') === 'desc' ? 'desc' : 'asc' };
  }

  _sortedData() {
    // Server mode: the host supplies the page pre-sorted (sort-change told
    // it what to fetch) — re-sorting the window here would disagree with
    // the dataset-wide order.
    if (this.serverTotal != null) return this._rows();
    const rows = this._filteredData();
    const eff = this.sort;
    if (!eff.field) return rows;
    const { field, direction } = eff;
    const dir = direction === 'desc' ? -1 : 1;
    // Stable-ish sort. Comparing typed: numbers, dates, then strings.
    const sortCol = this._columns.find((c) => c && c.field === field);
    const dateCol = !!sortCol && (sortCol.render === 'date' || sortCol.render === 'datetime');
    const copy = rows.slice();
    copy.sort((a, b) => {
      const av = refLabel(getField(a, field));
      const bv = refLabel(getField(b, field));
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      // A date column sorts by its day even when the host mapped it to text
      // ("Apr 3, 2027" sorted before "Jan 12, 2027").
      if (dateCol) {
        const ad = calendarDate(av);
        const bd = calendarDate(bv);
        if (ad && bd) return (ad - bd) * dir;
      }
      // Date compare FIRST. The numeric guard below accepts `-` as part of a
      // number, so an ISO date matched it and parseFloat('2026-02-01') became
      // 2026 — every row in the same year compared equal and date columns never
      // actually sorted. Date shape has to win before that branch is reached.
      if (isDateish(av) && isDateish(bv)) {
        const ad0 = Date.parse(av);
        const bd0 = Date.parse(bv);
        if (!isNaN(ad0) && !isNaN(bd0)) return (ad0 - bd0) * dir;
      }
      // Number compare
      const an = typeof av === 'number' ? av : parseFloat(av);
      const bn = typeof bv === 'number' ? bv : parseFloat(bv);
      if (!isNaN(an) && !isNaN(bn) && (typeof av !== 'string' || /^\s*[\d.,-]+\s*$/.test(av))) {
        return (an - bn) * dir;
      }
      // Date compare
      const ad = Date.parse(av);
      const bd = Date.parse(bv);
      if (!isNaN(ad) && !isNaN(bd) && typeof av === 'string' && /\d{4}-\d{2}-\d{2}|\//.test(av)) {
        return (ad - bd) * dir;
      }
      // String fallback
      return compareText(av, bv) * dir;
    });
    return copy;
  }

  _paginated(sorted) {
    const start = (this._page - 1) * this.pageSize;
    return sorted.slice(start, start + this.pageSize);
  }

  scheduleRender() {
    if (this._renderScheduled) return;
    this._renderScheduled = true;
    requestAnimationFrame(() => {
      this._renderScheduled = false;
      this._render();
    });
  }

  _render() {
    if (!this._root || !this.isConnected) return;
    const templates = this._templates;
    templates.begin();
    const cols = templates.has('row-actions')
      ? [...this._columns, { label: 'Actions', sortable: false, _extensionActions: true }]
      : this._columns;
    this._renderedCols = cols;
    this._pageIndex = new Map();
    // A render replaces the open editor's <input>. Note whether it had focus,
    // and where the caret was, so a host refresh mid-typing is invisible.
    const active = document.activeElement;
    const keepFocus = active && this._root.contains(active) && active.matches('[data-g-cell-input]')
      ? { start: active.selectionStart, end: active.selectionEnd }
      : null;
    const colspan = Math.max(1, cols.length);
    const serverMode = this.serverTotal != null;
    const sorted = this._sortedData();
    const totalPages = this._totalPages();
    // Server mode: .data IS the current page — no slicing; the range label
    // counts against the server-reported total and the page's actual length.
    const pageRows = serverMode ? sorted : this._paginated(sorted);
    // Counts what the reader can actually reach. With a query live, `_data` is
    // still the whole set, so using it would print "Showing 1–3 of 60".
    const total = serverMode ? this.serverTotal : sorted.length;
    const pageStart = total === 0 ? 0 : (this._page - 1) * this.pageSize;
    const pageEnd = serverMode
      ? Math.min(pageStart + pageRows.length, total)
      : Math.min(pageStart + this.pageSize, total);
    // data-row-index must index into _sortedData(): the page offset applies
    // only when the component sliced the page out itself.
    const indexBase = serverMode ? 0 : pageStart;

    this._detectChanges();
    const now = performance.now();

    const thead = `<thead><tr>${cols.map(c => this._renderHeader(c)).join('')}</tr></thead>`;

    let tbodyRows;
    if (this.loading) {
      // Skeleton rows, one cell per real column, so the table has its shape
      // (headers, widths) before the rows arrive. Widths vary a little per row.
      const widths = ['w-3/4', 'w-1/2', 'w-2/3', 'w-5/12', 'w-7/12'];
      tbodyRows = Array.from({ length: 5 }, (_, r) => `<tr aria-busy="true">${
        Array.from({ length: colspan }, (__, c) => `<td><div class="skeleton h-4 ${widths[(r + c) % widths.length]}"></div></td>`).join('')
      }</tr>`).join('');
    } else if (pageRows.length === 0) {
      // "No Folders found" under a live query reads as "this app has no
      // folders", which is the wrong thing to tell someone who just typed a
      // word — say what actually happened and name the query.
      const emptyMsg = this.search
        ? `No matches for “${escapeHtml(this.search)}”`
        : escapeHtml(this._sourceError ? `Couldn't load: ${this._sourceError}` : this.emptyText);
      const emptyIcon = this.search ? 'fa-regular fa-magnifying-glass' : this.emptyIcon;
      tbodyRows = `<tr><td colspan="${colspan}" class="text-center py-10 text-base-content/50"><i class="${escapeHtml(emptyIcon)} text-2xl mb-2 block"></i>${emptyMsg}</td></tr>`;
      if (templates.has('empty')) tbodyRows = `<tr><td colspan="${colspan}">${templates.region('empty')}</td></tr>`;
    } else {
      const statusSpec = this._statusSpec();
      tbodyRows = pageRows.map((row, i) => {
        const absoluteIndex = indexBase + i;
        this._pageIndex.set(this._rowKey(row), absoluteIndex);
        const cells = cols.map((c, ci) => {
          if (c._extensionActions) {
            return `<td${cellAttrs(c)}>${templates.region('row-actions', row, absoluteIndex) || ''}</td>`;
          }
          const content = templates.region(`cell:${c.field}`, row, absoluteIndex, c) || renderCell(row, c);
          if (this._isOpen(row, c)) {
            const e = this._editing;
            const editor = renderEditor(c, { draft: e.draft, error: e.error, remote: e.remote, size: this.size, label: c.label || c.field });
            const flashStyle = this._flashStyle(row, c, now);
            // The displayed content stays in the flow, invisible, so the cell
            // keeps the size it had; the editor is laid over it (status.js).
            // Without this the input's own width and height resize the column
            // and row on open and snap them back on save.
            return `<td${cellAttrs(c, { extraStyle: flashStyle })}${flashStyle ? ' data-g-flash' : ''} data-col="${ci}" data-g-editing><div data-g-cell-ghost aria-hidden="true" inert>${content}</div>${editor}</td>`;
          }
          const attrs = cellAttrs(c, { extraStyle: this._flashStyle(row, c, now) });
          const flash = attrs.includes('animation-delay') ? ' data-g-flash' : '';
          if (!isEditable(row, c, this.readonly)) return `<td${attrs}${flash}>${content}</td>`;
          return `<td${attrs}${flash}${this._editAttrs(row, c, ci)}>${content}</td>`;
        }).join('');
        const status = resolveStatus(row, absoluteIndex, statusSpec);
        const sAttr = statusAttr(status);
        // A tinted row drops daisyUI's row-hover. Its selector out-specifies the
        // tint's own hover rule, so leaving it on would repaint the row grey on
        // hover and throw the status away exactly when the user points at it.
        const cls = [
          (sAttr || this.noHover) ? '' : ROW_HOVER_CLASS,
          // Rows in a read-only table used to look clickable and do nothing.
          this.noRowClick ? '' : 'cursor-pointer',
        ].filter(Boolean).join(' ');
        const selAttr = (this._selectedId !== null && String(row && row._id) === this._selectedId)
          ? ' data-g-selected aria-selected="true"'
          : '';
        return `<tr class="${cls}" data-row-index="${absoluteIndex}"${sAttr}${selAttr}>${cells}</tr>`;
      }).join('');
    }
    const tbody = `<tbody>${tbodyRows}</tbody>`;

    const pagination = this._renderPagination(total, pageStart, pageEnd, totalPages);

    // `pin-rows` makes thead/tfoot sticky RELATIVE TO A SCROLL CONTAINER. The
    // wrapper has always been overflow-auto with no height, so it never
    // scrolled and there was nothing to stick within — the two ship together.
    // Defaulting a height when pin-rows is used alone keeps the attribute from
    // being a silent no-op.
    const maxH = this.maxHeight || (this.hasAttribute('pin-rows') ? '70vh' : null);
    const wrapStyle = maxH ? ` style="max-height:${maxH}"` : '';
    const tableCls = [
      'table', `table-${this.size}`,
      this.hasAttribute('zebra') ? 'table-zebra' : '',
      this.hasAttribute('pin-rows') ? 'table-pin-rows' : '',
    ].filter(Boolean).join(' ');

    templates.commit(`${templates.region('header')}
      <div class="overflow-auto"${wrapStyle}>
        <table class="${tableCls}">
          ${thead}
          ${tbody}
          ${this._renderFoot(cols, sorted, serverMode, colspan)}
        </table>
      </div>
      ${pagination}
    `);
    this._restoreFocus(keepFocus);

    // What the chrome around this table needs in order to decide anything —
    // principally whether a search box is worth showing. Emitted after the
    // render so a listener reading `searchRelevant` sees the settled state.
    // <g-panel> only toggles an attribute in response, so this cannot loop.
    this.dispatchEvent(new CustomEvent('content-change', {
      detail: { total, rows: pageRows.length, searchRelevant: this.searchRelevant },
      bubbles: true,
      composed: true,
    }));
  }

  // Totals row. Renders only when at least one column asks for one, so a table
  // without totals gets no empty footer band.
  _renderFoot(cols, sorted, serverMode, colspan) {
    if (!cols.some(c => TOTALS.has(c.total))) return '';
    const cells = cols.map(c => {
      const attrs = cellAttrs(c);
      if (!TOTALS.has(c.total)) return `<td${attrs}></td>`;
      if (serverMode) {
        // .data is one page here, so any total computed from it would be a
        // page-total wearing a dataset-total's clothes. Say nothing instead —
        // unless the host supplies the real figure.
        const supplied = this._totals && this._totals[c.field];
        if (!Number.isFinite(supplied)) {
          return `<td${attrs} title="Totals are unavailable in server-pagination mode; set .totals to supply them">—</td>`;
        }
        return `<td${attrs}>${formatTotal(supplied, c)}</td>`;
      }
      // Aggregate the WHOLE dataset, not the page — a total that changes when
      // you paginate is worse than no total at all.
      const value = aggregate(sorted, { metric: c.total, field: c.field });
      return `<td${attrs}>${formatTotal(value, c)}</td>`;
    }).join('');
    return `<tfoot><tr>${cells}</tr></tfoot>`;
  }

  _renderHeader(col) {
    // An actions column has no `field`, so the usual `label || field` fallback
    // would print the word "undefined" above the buttons.
    const label = escapeHtml(col.label || (col.render === 'actions' ? '' : col.field));
    const attrs = cellAttrs(col, { header: true });
    if (!col.sortable) return `<th${attrs}>${label}</th>`;
    const eff = this.sort;
    const isActive = eff.field === col.field;
    const arrow = isActive ? (eff.direction === 'desc' ? ' ↓' : ' ↑') : '';
    return `<th${attrs} class="cursor-pointer select-none" data-sort-field="${escapeHtml(col.field)}"${isActive ? ` aria-sort="${eff.direction === 'desc' ? 'descending' : 'ascending'}"` : ''}>${label}<span class="opacity-60">${arrow}</span></th>`;
  }

  _renderPagination(total, pageStart, pageEnd, totalPages) {
    const range = this.loading ? '' : (total > 0 ? `Showing ${pageStart + 1}–${pageEnd} of ${total}` : 'No results');
    return `
      <div class="border-t border-base-300 px-5 py-3 flex justify-between items-center">
        <span class="text-sm text-base-content/60 truncate">${escapeHtml(range)}</span>
        <div class="join">
          <button class="join-item btn btn-sm" data-page="prev"${this._page <= 1 ? ' disabled' : ''}>«</button>
          <button class="join-item btn btn-sm">${this._page}</button>
          <button class="join-item btn btn-sm" data-page="next"${this._page >= totalPages ? ' disabled' : ''}>»</button>
        </div>
      </div>
    `;
  }

  // ── Change highlight ──
  //
  // Diffed once per data update, against every row rather than the page, so
  // a record that changed while on another page doesn't flash when paged to.

  _detectChanges() {
    if (this._diffedVersion === this._dataVersion || this.loading) return;
    this._diffedVersion = this._dataVersion;
    const fields = [...new Set(this._columns.map(c => c && c.field).filter(Boolean))];
    const next = snapshotRows(this._rows(), fields, getField);
    const now = performance.now();
    const changed = changedCells(this._valueSnap, next);
    for (const k of changed) this._flash.set(k, now);
    this._valueSnap = next;
    // The open cell is drawn from its own draft, so a change to it reaches
    // the editor here or not at all.
    const e = this._editing;
    if (e && e.id != null && changed.includes(flashKey(e.id, e.field))) {
      const row = this._liveRow(e);
      const col = this._columns.find(c => c && c.field === e.field);
      if (row && col) Object.assign(e, reconcileOpenEdit(e, getField(row, e.field), col));
    }
  }

  /** `animation-delay` that resumes a running fade where it was, or null. */
  _flashStyle(row, col, now) {
    if (!this._flash.size || !row || row._id == null || !col.field) return null;
    const key = flashKey(String(row._id), col.field);
    const start = this._flash.get(key);
    if (start == null) return null;
    const elapsed = now - start;
    if (elapsed >= FLASH_MS) { this._flash.delete(key); return null; }
    // The user's own save, or one being rolled back, already says so in place.
    if (this._cellStates(row).has(col.field)) return null;
    // Every render rebuilds the cell, which would restart the animation; a
    // negative delay starts it part-way through instead.
    return `animation-delay:-${Math.round(elapsed)}ms`;
  }

  // ── Inline editing ──
  //
  // Opt-in per column (`editable`), so a table nobody configured for it is
  // byte-for-byte what it was. The table writes the new value into the row and
  // emits `cell-edit`; persisting it is the host's job, reported back through
  // `respondWith(promise)` so a failed save can be undone in place rather than
  // leaving a cell that shows a value the server never accepted.

  _isOpen(row, col) {
    const e = this._editing;
    if (!e || !col || e.field !== col.field) return false;
    if (e.row === row) return true;
    // The host re-assigned `.data` mid-edit (a poll, a refetch): same record,
    // new object. Follow it by id so the editor and the draft survive.
    return e.id != null && row && row._id != null && String(row._id) === e.id;
  }

  // Rows with an _id are tracked by id: a live update replaces the record
  // object, and a "saving…" cell must stay saving across that swap.
  _rowKey(row) { return row && row._id != null ? `id:${row._id}` : row; }

  _cellStates(row) {
    const byId = row && row._id != null;
    const store = byId ? this._cellStateById : this._cellState;
    const key = byId ? this._rowKey(row) : row;
    let m = store.get(key);
    if (!m) { m = new Map(); store.set(key, m); }
    return m;
  }

  _editAttrs(row, col, ci) {
    const kind = editorFor(col);
    const byId = row && row._id != null;
    const st = (byId ? this._cellStateById.get(this._rowKey(row)) : this._cellState.get(row))?.get(col.field);
    let out = ` data-col="${ci}" data-g-edit="${kind}" tabindex="0"`;
    if (st && st.saving) out += ' data-g-saving aria-busy="true"';
    else if (st && st.error) out += ` data-g-cell-error title="${escapeHtml(`Not saved: ${st.error}`)}"`;
    else out += ` title="${kind === 'boolean' ? 'Click to toggle' : 'Click to edit'}"`;
    return out;
  }

  _restoreFocus(keepFocus) {
    const f = this._afterRender;
    this._afterRender = null;
    const input = this._root.querySelector('[data-g-cell-input]');
    // The open cell's row left the page (a sort, a search, a page click): the
    // session has nowhere to render, so it ends rather than lingering unseen.
    if (this._editing && !input && !this.loading) this._editing = null;
    if (input && ((f && f.editor) || keepFocus)) {
      input.focus();
      try {
        if (f && f.select && input.select) input.select();
        else if (keepFocus && keepFocus.start != null) input.setSelectionRange(keepFocus.start, keepFocus.end);
      } catch { /* date and select inputs have no text selection */ }
      if (f && f.picker && input.tagName === 'SELECT') {
        try { input.showPicker?.(); } catch { /* no picker without a user gesture */ }
      }
    } else if (f && f.row) {
      const td = this._cellEl(f.row, f.field);
      if (td) td.focus();
    }
  }

  _cellEl(row, field) {
    const idx = this._pageIndex && this._pageIndex.get(this._rowKey(row));
    const ci = (this._renderedCols || []).findIndex(c => c && !c._extensionActions && c.field === field);
    if (idx == null || ci < 0) return null;
    return this._root.querySelector(`tr[data-row-index="${idx}"] > td[data-col="${ci}"]`);
  }

  /** The row + column a rendered cell stands for. */
  _cellAt(td) {
    const tr = td && td.closest('tr[data-row-index]');
    if (!tr || !this.contains(tr)) return null;
    const row = this._sortedData()[parseInt(tr.getAttribute('data-row-index'), 10)];
    const col = (this._renderedCols || [])[parseInt(td.getAttribute('data-col'), 10)];
    return row && col ? { row, col } : null;
  }

  /** The live row object for the open session, following a `.data` swap by id. */
  _liveRow(e) {
    const rows = this._rows();
    if (rows.includes(e.row)) return e.row;
    if (e.id == null) return null;
    return rows.find(r => r && r._id != null && String(r._id) === e.id) || null;
  }

  _beginEdit(row, col, { picker = false } = {}) {
    if (!isEditable(row, col, this.readonly)) return;
    if (this._editing) this._commitEdit({ flush: true });
    const states = this._cellStates(row);
    if (states.get(col.field)?.error) states.delete(col.field);
    if (editorFor(col) === 'boolean') {
      // Nothing to type: a click is the whole edit.
      this._applyEdit(row, col, toggled(getField(row, col.field)));
      this._afterRender = { row, field: col.field };
      this._render();
      return;
    }
    const draft = draftFor(getField(row, col.field), col);
    this._editing = {
      row, id: row && row._id != null ? String(row._id) : null,
      field: col.field, draft, initial: draft, error: null,
    };
    // Opened by a click: a dropdown opens its list on that same click, rather
    // than sitting closed and focused until a second one (QA B05-08).
    this._afterRender = { editor: true, select: true, picker };
    this._render();
  }

  _cancelEdit() {
    const e = this._editing;
    if (!e) return;
    this._editing = null;
    this._afterRender = { row: this._liveRow(e), field: e.field };
    this._render();
  }

  /**
   * Close the open cell, saving what was typed.
   *
   *   next  { row, col } to open afterwards (Tab)
   *   blur  focus already went elsewhere: do not pull it back
   *   flush another cell is opening: no render, and an invalid draft is dropped
   *         rather than holding the user in a cell they just clicked out of
   */
  _commitEdit({ next = null, blur = false, flush = false } = {}) {
    const e = this._editing;
    if (!e) return;
    const input = this._root.querySelector('[data-g-cell-input]');
    const raw = input ? input.value : e.draft;
    const row = this._liveRow(e);
    const col = this._columns.find(c => c && c.field === e.field);
    if (!row || !col || raw === e.initial) {
      this._editing = null;
    } else {
      const parsed = parseDraft(raw, col, row);
      if (parsed.error && !flush) {
        e.draft = raw;
        e.error = parsed.error;
        this._afterRender = blur ? null : { editor: true };
        this._render();
        return;
      }
      this._editing = null;
      if (!parsed.error && !sameValue(parsed.value, getField(row, col.field))) this._applyEdit(row, col, parsed.value);
    }
    if (flush) return;
    if (next && editorFor(next.col) !== 'boolean') {
      this._beginEdit(next.row, next.col);
      return;
    }
    this._afterRender = next ? { row: next.row, field: next.col.field } : (blur ? null : { row, field: e.field });
    this._render();
  }

  /** Write the value, tell the host, and track the save it reports back. */
  _applyEdit(row, col, value) {
    const field = col.field;
    const previousValue = getField(row, field);
    const waits = [];
    let open = true;
    const ev = new CustomEvent('cell-edit', {
      detail: {
        field, value, previousValue, row, column: col,
        index: this._sortedData().indexOf(row),
        respondWith: (p) => {
          if (!open) throw new Error('[g-table] respondWith() must be called synchronously inside the cell-edit listener');
          waits.push(p);
        },
      },
      bubbles: true, composed: true, cancelable: true,
    });
    this.dispatchEvent(ev);
    open = false;
    // Vetoed: the cell keeps what it had, as if the edit never happened.
    if (ev.defaultPrevented) return false;
    // The user made this change; the update that echoes it back is no news.
    if (row._id != null) this._valueSnap?.get(String(row._id))?.set(field, signature(value));
    const states = this._cellStates(row);
    const c = this._source.collection;
    if (!waits.length && c && row._id != null) {
      // Nobody handled it and the rows came from `source`: save through the
      // collection. It applies the change to every component on the page at
      // once — the KPIs move as the cell does — PUTs it, and undoes it there
      // if the server refuses. The socket broadcast then reaches other users.
      waits.push(c.update(row._id, { [field]: value }));
    } else {
      row[field] = value;
    }
    if (!waits.length) { states.delete(field); return true; }
    const token = {};
    states.set(field, { saving: true, token });
    Promise.all(waits).then(() => {
      if (states.get(field)?.token === token) states.delete(field);
      this.scheduleRender();
    }, (err) => {
      // A later edit to the same cell owns it now; this failure is stale.
      if (states.get(field)?.token !== token) return;
      if (row[field] === value) row[field] = previousValue;
      const msg = (err && err.message) || (typeof err === 'string' && err) || 'Could not save';
      states.set(field, { error: msg });
      this.scheduleRender();
    });
    return true;
  }

  /** The editable cell before/after `td` in reading order, as { row, col }. */
  _neighbour(td, dir) {
    const cells = [...this._root.querySelectorAll('td[data-g-edit], td[data-g-editing]')];
    const i = cells.indexOf(td);
    return i < 0 ? null : this._cellAt(cells[i + dir]);
  }

  _onKeydown = (e) => {
    const t = e.target;
    if (!(t instanceof Element) || !this._root || !this._root.contains(t)) return;
    if (t.matches('[data-g-cell-input]')) {
      if (e.key === 'Escape') { e.preventDefault(); this._cancelEdit(); }
      else if (e.key === 'Enter' && (t.tagName !== 'TEXTAREA' || e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        this._commitEdit();
      } else if (e.key === 'Tab') {
        const next = this._neighbour(t.closest('td'), e.shiftKey ? -1 : 1);
        // No neighbour: let Tab leave the table; focusout saves.
        if (next) { e.preventDefault(); this._commitEdit({ next }); }
      }
      return;
    }
    if (t.matches('td[data-g-edit]') && (e.key === 'Enter' || e.key === 'F2'
      || (e.key === ' ' && t.getAttribute('data-g-edit') === 'boolean'))) {
      const hit = this._cellAt(t);
      if (hit) { e.preventDefault(); this._beginEdit(hit.row, hit.col); }
    }
  };

  _onInput = (e) => {
    if (this._editing && e.target instanceof Element && e.target.matches('[data-g-cell-input]')) {
      this._editing.draft = e.target.value;
    }
  };

  // A pick from a list is the whole edit — no reason to make the user also
  // press Enter or click away.
  _onChange = (e) => {
    if (this._editing && e.target instanceof Element && e.target.matches('select[data-g-cell-input]')) {
      this._commitEdit();
    }
  };

  // Deferred a tick, as in g-tree: committing re-renders, and doing it inside
  // focusout would replace the very element the user is clicking — another
  // cell, a page button — so that click would never land.
  //
  // A customizable select (DaisyUI's `appearance: base-select`) moves focus
  // onto its own <option>s when its list opens. That is still the cell, not
  // leaving it: committing there closed the list as it opened (QA B05-08,
  // 2026-10-23). The option's own focusout, when the user clicks away, is
  // what ends the edit.
  _onFocusOut = (e) => {
    const input = e.target instanceof Element ? e.target.closest('[data-g-cell-input]') : null;
    if (!input || !this.contains(input)) return;
    const session = this._editing;
    if (!session) return;
    setTimeout(() => {
      // Disconnected = a re-render swapped this input for a fresh one carrying
      // the draft. Not a commit.
      if (this._editing !== session || !input.isConnected || input.contains(document.activeElement)) return;
      this._commitEdit({ blur: true });
    }, 0);
  };

  _handleClick = (e) => {
    // Row actions first. The row-click branch below already declines to fire on
    // a button, so this is the only thing that can turn that click into a
    // signal — without it the button would be inert and the row would look
    // broken rather than un-actioned.
    const actionBtn = e.target.closest('button[data-row-action]');
    if (actionBtn && this.contains(actionBtn)) {
      const tr = actionBtn.closest('tr[data-row-index]');
      const idx = tr ? parseInt(tr.getAttribute('data-row-index'), 10) : -1;
      const item = idx >= 0 ? this._sortedData()[idx] : null;
      if (item) {
        this.dispatchEvent(new CustomEvent('row-action', {
          detail: { action: actionBtn.getAttribute('data-row-action'), row: item, index: idx },
          bubbles: true,
          composed: true,
        }));
      }
      return;
    }
    const sortHeader = e.target.closest('[data-sort-field]');
    if (sortHeader && this.contains(sortHeader)) {
      const field = sortHeader.getAttribute('data-sort-field');
      const cur = this.sort;
      const nextDir = (cur.field === field && cur.direction === 'asc') ? 'desc' : 'asc';
      this.setSort(field, nextDir);
      return;
    }
    const pageBtn = e.target.closest('[data-page]');
    if (pageBtn && this.contains(pageBtn)) {
      const dir = pageBtn.getAttribute('data-page');
      this.setPage(this._page + (dir === 'next' ? 1 : -1));
      return;
    }
    // An editable cell owns its click: it opens the editor instead of firing
    // row-click, and clicks inside an open editor are the user typing.
    const cell = e.target.closest('td[data-col]');
    if (cell && this.contains(cell)) {
      if (cell.hasAttribute('data-g-editing')) return;
      if (cell.hasAttribute('data-g-edit') && !ownsRecordClick(e, cell)) {
        const hit = this._cellAt(cell);
        if (hit) { this._beginEdit(hit.row, hit.col, { picker: true }); return; }
      }
    }
    const row = e.target.closest('tr[data-row-index]');
    if (row && this.contains(row)) {
      // Don't fire row-click on interactive children
      if (ownsRecordClick(e, row)) return;
      const idx = parseInt(row.getAttribute('data-row-index'), 10);
      const sorted = this._sortedData();
      const item = sorted[idx];
      if (item) {
        this.dispatchEvent(new CustomEvent('row-click', {
          detail: { row: item, index: idx },
          bubbles: true,
          composed: true,
        }));
      }
    }
  };
}

if (!customElements.get('g-table')) {
  customElements.define('g-table', GTable);
}
